import { useMemo, type ReactElement } from 'react';
import type { WorkoutSplit, WorkoutSwimSplit, Discipline } from '@/lib/workouts';

/**
 * Tabla de splits/laps (M2.15 / M2.17 / M2.40).
 *
 * Modo run/bike/other:
 *   Columnas: Km · Tiempo · Pace · ΔElev · HR · Power.
 *   - `discipline === 'bike'`: agrupa cada 5 km. Cada fila representa el
 *     tramo "1-5", "6-10", etc. HR y power se promedian ponderados por
 *     tiempo, elev se suma, pace se recalcula como suma timeS / suma km.
 *   - resto de disciplinas: 1 fila por km.
 *
 * Modo swim (M2.40):
 *   Columnas: 100m · Tiempo · Pace · HR. La piscina es plana → sin ΔElev
 *   y sin Power. Las filas representan bloques de ~100m parseados desde
 *   los `laps[]` de Strava (1 lap por largo de piscina). El último bloque
 *   puede ser parcial (50m / 75m).
 *   - Si `discipline === 'swim'` pero `splitsSwim` está vacío/null → muestra
 *     mensaje explicando que la activity no expone laps utilizables.
 *
 * Coloring de la celda Pace: comparada con la MEDIANA de paces válidos del
 * conjunto (ya agrupado si procede).
 *   - Más rápido que la mediana (pace menor): degradado a verde.
 *   - Más lento: degradado a rojo.
 *   - ±2% de la mediana: neutro.
 *
 * Footer con totales: tiempo acumulado, pace medio (ponderado por tiempo),
 * desnivel acumulado, HR media (ponderada por tiempo).
 * En swim el footer cambia: distancia total en metros (suma de distanceM)
 * y pace medio MM:SS / 100m.
 */
export interface SplitsTableProps {
  splits: WorkoutSplit[];
  /** Disciplina del actual; bike agrupa cada 5 km, swim usa splitsSwim. */
  discipline?: Discipline;
  /**
   * M2.40 — splits cada 100m de natación. Solo se consume si
   * `discipline === 'swim'`. Para el resto de disciplinas se ignora.
   */
  splitsSwim?: WorkoutSwimSplit[] | null;
}

/**
 * Fila renderizable. Incluye los campos derivados pero añade una etiqueta
 * para la columna Km, que en bike es un rango ("6-10") y en el resto un
 * número simple.
 */
interface SplitRow {
  /** Etiqueta para la columna Km. */
  kmLabel: string;
  /** Identificador único de la fila (último km del grupo o el propio km). */
  rowKey: string;
  /** Identificador estable para data-testid. */
  testIdSuffix: string | number;
  timeS: number;
  paceSecPerKm: number | null;
  avgHr: number | null;
  avgPower: number | null;
  elevDiffM: number | null;
}

export function SplitsTable({
  splits,
  discipline = 'other',
  splitsSwim = null,
}: SplitsTableProps): ReactElement {
  // M2.40 — Modo swim: tabla diferente (100m, sin power, sin elev).
  if (discipline === 'swim') {
    return <SwimSplitsTable splitsSwim={splitsSwim} />;
  }

  const rows = useMemo<SplitRow[]>(() => {
    if (splits.length === 0) return [];
    if (discipline === 'bike') {
      return groupSplitsBy(splits, 5);
    }
    return splits.map((s) => ({
      kmLabel: String(s.km),
      rowKey: `split-${s.km}`,
      testIdSuffix: s.km,
      timeS: s.timeS,
      paceSecPerKm: s.paceSecPerKm,
      avgHr: s.avgHr,
      avgPower: s.avgPower,
      elevDiffM: s.elevDiffM,
    }));
  }, [splits, discipline]);

  if (splits.length === 0) {
    return (
      <p
        data-testid="splits-empty"
        className="text-sm text-slate-600"
      >
        Sin splits disponibles para este entreno.
      </p>
    );
  }

  // Mediana de pace para colorear celdas (sobre las filas finales).
  const paces = rows
    .map((r) => r.paceSecPerKm)
    .filter((p): p is number => typeof p === 'number');
  const medianPace = paces.length > 0 ? median(paces) : null;

  const totals = computeTotalsFromRows(rows);

  // M2.41 — Para bici añadimos columna Velocidad (km/h) junto a Pace.
  // La unidad nativa del ciclista es km/h, no min/km. paceSecPerKm = 180s
  // (3 min/km) → 60/3 = 20 km/h.
  const showSpeed = discipline === 'bike';

  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-full text-sm"
        data-testid="splits-table"
      >
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            <th scope="col" className="py-2 pr-3">Km</th>
            <th scope="col" className="py-2 pr-3">Tiempo</th>
            <th scope="col" className="py-2 pr-3">Pace</th>
            {showSpeed && (
              <th scope="col" className="py-2 pr-3" data-testid="splits-col-speed">
                Velocidad
              </th>
            )}
            <th scope="col" className="py-2 pr-3">ΔElev</th>
            <th scope="col" className="py-2 pr-3">FC</th>
            <th scope="col" className="py-2 pr-3">Potencia</th>
          </tr>
        </thead>
        <tbody className="text-slate-800">
          {rows.map((r) => (
            <tr key={r.rowKey} className="border-b border-slate-100">
              <th scope="row" className="py-1.5 pr-3 font-medium">{r.kmLabel}</th>
              <td className="py-1.5 pr-3 tabular-nums">{formatTime(r.timeS)}</td>
              <td
                className={`py-1.5 pr-3 tabular-nums ${paceColorClass(r.paceSecPerKm, medianPace)}`}
                data-testid={`split-pace-${r.testIdSuffix}`}
              >
                {formatPace(r.paceSecPerKm)}
              </td>
              {showSpeed && (
                <td
                  className="py-1.5 pr-3 tabular-nums"
                  data-testid={`split-speed-${r.testIdSuffix}`}
                >
                  {formatSpeedKmh(r.paceSecPerKm)}
                </td>
              )}
              <td className="py-1.5 pr-3 tabular-nums">{formatElev(r.elevDiffM)}</td>
              <td className="py-1.5 pr-3 tabular-nums">
                {r.avgHr !== null ? `${r.avgHr} bpm` : '—'}
              </td>
              <td className="py-1.5 pr-3 tabular-nums">
                {r.avgPower !== null ? `${r.avgPower} W` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
            <th scope="row" className="py-2 pr-3">Total</th>
            <td className="py-2 pr-3 tabular-nums">{formatTime(totals.totalSec)}</td>
            <td className="py-2 pr-3 tabular-nums">{formatPace(totals.avgPace)}</td>
            {showSpeed && (
              <td className="py-2 pr-3 tabular-nums">
                {formatSpeedKmh(totals.avgPace)}
              </td>
            )}
            <td className="py-2 pr-3 tabular-nums">{formatElev(totals.totalElev)}</td>
            <td className="py-2 pr-3 tabular-nums">
              {totals.avgHr !== null ? `${Math.round(totals.avgHr)} bpm` : '—'}
            </td>
            <td className="py-2 pr-3 tabular-nums">
              {totals.avgPower !== null ? `${Math.round(totals.avgPower)} W` : '—'}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * M2.41 — Convierte pace (s/km) a velocidad (km/h). Devuelve '—' si null o ≤0.
 * km/h = 3600 / paceSecPerKm.
 */
function formatSpeedKmh(paceSecPerKm: number | null): string {
  if (paceSecPerKm === null || paceSecPerKm <= 0) return '—';
  const kmh = 3600 / paceSecPerKm;
  return `${kmh.toFixed(1)} km/h`;
}

// ---------------------------------------------------------------------------
// Modo swim (M2.40)
// ---------------------------------------------------------------------------
//
// Tabla específica para natación. No reutilizamos `SplitRow` porque la unidad
// (100m vs km) y las columnas son distintas — fusionarlo con un campo opcional
// más complicaría el render. Mantenerlo aparte deja el modo común intacto.

interface SwimSplitsTableProps {
  splitsSwim: WorkoutSwimSplit[] | null;
}

function SwimSplitsTable({ splitsSwim }: SwimSplitsTableProps): ReactElement {
  // Mediana de pace para colorear celdas (usando solo paces válidos).
  const rows = splitsSwim ?? [];

  if (rows.length === 0) {
    return (
      <p
        data-testid="splits-swim-empty"
        className="text-sm text-slate-600"
      >
        Sin splits de natación disponibles (la activity no expone laps).
      </p>
    );
  }

  const paces = rows
    .map((r) => r.paceSecPer100m)
    .filter((p): p is number => typeof p === 'number');
  const medianPace = paces.length > 0 ? median(paces) : null;

  // Totales: distancia total en metros + pace medio ponderado por distancia
  // (no por tiempo, porque distinto del run: queremos seg / 100m promedio).
  let totalDist = 0;
  let totalTime = 0;
  let hrWeighted = 0;
  let hrWeight = 0;
  for (const r of rows) {
    totalDist += r.distanceM;
    totalTime += r.timeS;
    if (typeof r.avgHr === 'number') {
      hrWeighted += r.avgHr * r.timeS;
      hrWeight += r.timeS;
    }
  }
  const avgPace = totalDist > 0 ? Math.round((totalTime / totalDist) * 100) : null;
  const avgHr = hrWeight > 0 ? Math.round(hrWeighted / hrWeight) : null;

  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-full text-sm"
        data-testid="splits-table"
      >
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            <th scope="col" className="py-2 pr-3">100m</th>
            <th scope="col" className="py-2 pr-3">Tiempo</th>
            <th scope="col" className="py-2 pr-3">Pace</th>
            <th scope="col" className="py-2 pr-3">FC</th>
          </tr>
        </thead>
        <tbody className="text-slate-800">
          {rows.map((r) => (
            <tr key={`swim-${r.block}`} className="border-b border-slate-100">
              <th scope="row" className="py-1.5 pr-3 font-medium">{r.block}</th>
              <td className="py-1.5 pr-3 tabular-nums">{formatTime(r.timeS)}</td>
              <td
                className={`py-1.5 pr-3 tabular-nums ${paceColorClass(r.paceSecPer100m, medianPace)}`}
                data-testid={`split-pace-${r.block}`}
              >
                {formatSwimPace(r.paceSecPer100m)}
              </td>
              <td className="py-1.5 pr-3 tabular-nums">
                {r.avgHr !== null ? `${r.avgHr} bpm` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
            <th
              scope="row"
              className="py-2 pr-3"
              data-testid="splits-swim-total-distance"
            >
              {`${totalDist} m`}
            </th>
            <td className="py-2 pr-3 tabular-nums">{formatTime(totalTime)}</td>
            <td className="py-2 pr-3 tabular-nums">{formatSwimPace(avgPace)}</td>
            <td className="py-2 pr-3 tabular-nums">
              {avgHr !== null ? `${avgHr} bpm` : '—'}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * Formato MM:SS sin sufijo — el header ya indica "100m".
 */
function formatSwimPace(sec: number | null): string {
  if (typeof sec !== 'number' || !Number.isFinite(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${m}:${pad(s)}`;
}

// ---------------------------------------------------------------------------
// Agrupamiento (M2.17)
// ---------------------------------------------------------------------------

/**
 * Agrupa splits en bloques de `size` km. Cada split nominal cuenta como 1 km
 * (Strava usa `splits_metric` por km exacto). HR/Power se promedian
 * ponderados por tiempo (ignorando nulls). Elev se suma. Pace se recalcula
 * como timeS_total / km_total.
 */
function groupSplitsBy(splits: WorkoutSplit[], size: number): SplitRow[] {
  const rows: SplitRow[] = [];
  for (let i = 0; i < splits.length; i += size) {
    const slice = splits.slice(i, i + size);
    if (slice.length === 0) continue;
    const first = slice[0]!;
    const last = slice[slice.length - 1]!;
    const totalTime = slice.reduce((s, x) => s + x.timeS, 0);
    const totalDistKm = slice.length; // 1 km por split nominalmente
    const pace = totalDistKm > 0 ? Math.round(totalTime / totalDistKm) : null;

    const weightedHr = weightedAvg(slice.map((s) => [s.avgHr, s.timeS]));
    const weightedPwr = weightedAvg(slice.map((s) => [s.avgPower, s.timeS]));
    const elev = sumNullable(slice.map((s) => s.elevDiffM));

    const kmLabel =
      first.km === last.km ? `${first.km}` : `${first.km}-${last.km}`;

    rows.push({
      kmLabel,
      rowKey: `bike-group-${first.km}-${last.km}`,
      testIdSuffix: last.km,
      timeS: totalTime,
      paceSecPerKm: pace,
      avgHr: weightedHr !== null ? Math.round(weightedHr) : null,
      avgPower: weightedPwr !== null ? Math.round(weightedPwr) : null,
      elevDiffM: elev,
    });
  }
  return rows;
}

/**
 * Promedio ponderado: pares `[valor | null, peso]`. Si todos los valores son
 * null o todos los pesos son 0 → null.
 */
function weightedAvg(pairs: Array<[number | null, number]>): number | null {
  let sumWV = 0;
  let sumW = 0;
  for (const [v, w] of pairs) {
    if (typeof v !== 'number' || !Number.isFinite(v) || w <= 0) continue;
    sumWV += v * w;
    sumW += w;
  }
  return sumW > 0 ? sumWV / sumW : null;
}

/**
 * Suma una lista que puede contener nulls. Devuelve null si TODOS son null.
 */
function sumNullable(values: Array<number | null>): number | null {
  let sum = 0;
  let has = false;
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v)) {
      sum += v;
      has = true;
    }
  }
  return has ? Math.round(sum * 10) / 10 : null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function median(arr: number[]): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const n = sorted.length;
  if (n === 0) return 0;
  const mid = Math.floor(n / 2);
  if (n % 2 === 1) {
    return sorted[mid] ?? 0;
  }
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

interface TotalsRow {
  totalSec: number;
  avgPace: number | null;
  totalElev: number | null;
  avgHr: number | null;
  avgPower: number | null;
}

function computeTotalsFromRows(rows: SplitRow[]): TotalsRow {
  let totalSec = 0;
  let elevSum = 0;
  let hasElev = false;
  let hrWeighted = 0;
  let hrTimeWeight = 0;
  let powerWeighted = 0;
  let powerTimeWeight = 0;
  let paceWeighted = 0;
  let paceTimeWeight = 0;

  for (const r of rows) {
    totalSec += r.timeS;
    if (typeof r.elevDiffM === 'number') {
      elevSum += r.elevDiffM;
      hasElev = true;
    }
    if (typeof r.avgHr === 'number') {
      hrWeighted += r.avgHr * r.timeS;
      hrTimeWeight += r.timeS;
    }
    if (typeof r.avgPower === 'number') {
      powerWeighted += r.avgPower * r.timeS;
      powerTimeWeight += r.timeS;
    }
    if (typeof r.paceSecPerKm === 'number') {
      paceWeighted += r.paceSecPerKm * r.timeS;
      paceTimeWeight += r.timeS;
    }
  }

  return {
    totalSec,
    avgPace: paceTimeWeight > 0 ? Math.round(paceWeighted / paceTimeWeight) : null,
    totalElev: hasElev ? Math.round(elevSum * 10) / 10 : null,
    avgHr: hrTimeWeight > 0 ? hrWeighted / hrTimeWeight : null,
    avgPower: powerTimeWeight > 0 ? powerWeighted / powerTimeWeight : null,
  };
}

function paceColorClass(
  pace: number | null,
  medianPace: number | null,
): string {
  if (typeof pace !== 'number' || typeof medianPace !== 'number' || medianPace === 0) {
    return '';
  }
  const delta = (pace - medianPace) / medianPace;
  // Más rápido (pace menor) → verde; más lento → rojo. ±2% neutro.
  if (delta <= -0.05) return 'bg-emerald-100 text-emerald-900 font-semibold';
  if (delta <= -0.02) return 'bg-emerald-50 text-emerald-800';
  if (delta >= 0.05) return 'bg-rose-100 text-rose-900 font-semibold';
  if (delta >= 0.02) return 'bg-rose-50 text-rose-800';
  return '';
}

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec - h * 3600) / 60);
  const s = Math.floor(sec - h * 3600 - m * 60);
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
}

function formatPace(sec: number | null): string {
  if (typeof sec !== 'number' || !Number.isFinite(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${m}:${pad(s)}/km`;
}

function formatElev(m: number | null): string {
  if (typeof m !== 'number' || !Number.isFinite(m)) return '—';
  const sign = m > 0 ? '+' : '';
  return `${sign}${m.toFixed(1)} m`;
}
